/** Directional arc stroke — sword slashes, cleave trails, sweep attacks.
 *
 * Renders a curved arc stroke at a configurable radius and sweep angle.
 * Canvas2D provides native antialiasing in pixelArt mode. The arc texture
 * is drawn once (symmetric around angle 0); Phaser rotation orients it
 * to the attack direction at play time.
 */
import Phaser from 'phaser';
import { VfxPrimitive, VfxPrimitiveConfig } from './VfxPrimitive';
import { CRISP_SCALE } from '../../ui/CrispUtil';

export interface SlashArcConfig extends VfxPrimitiveConfig {
  /** Radius of the arc from center, in logical pixels. */
  arcRadius: number;
  /** Stroke width of the arc line, in logical pixels. */
  arcWidth: number;
  /** Total sweep angle, in radians (e.g. PI/2 for 90 degrees). */
  sweepAngle: number;
  /** Default center direction, in radians (0 = right). Overridable via setDirection(). */
  direction: number;
  /** Total animation duration in ms. */
  durationMs: number;
  /** Fraction of durationMs spent fading out (0–1). Remainder is the appear/hold phase. */
  fadeRatio: number;
  /** Starting scale multiplier for the grow-in (default 0.3). */
  scaleStart?: number;
}

const POOL_SIZE = 2;

export class SlashArc extends VfxPrimitive<SlashArcConfig> {
  private textureKey!: string;
  private poolIndex = 0;
  private currentDirection: number;

  constructor(scene: Phaser.Scene, config: SlashArcConfig) {
    super(scene, config);
    this.currentDirection = config.direction;
  }

  /** Set the arc direction before calling play(). Config direction is added as an offset. */
  setDirection(angle: number): void {
    this.currentDirection = angle + this.config.direction;
  }

  protected allocatePool(): void {
    this.textureKey = this.ensureArcTexture();
    for (let i = 0; i < POOL_SIZE; i++) {
      const img = this.createPoolImage(this.textureKey);
      img.setOrigin(0.5, 0.5);
    }
  }

  play(x: number, y: number, depth: number): void {
    const { config } = this;
    const img = this.pool[this.poolIndex % this.pool.length];
    this.poolIndex++;

    this.scene.tweens.killTweensOf(img);
    this.activate(img, x, y, depth);

    const scaleStart = (config.scaleStart ?? 0.3) / CRISP_SCALE;
    const scaleFull = 1 / CRISP_SCALE;
    img.setRotation(this.currentDirection);
    img.setScale(scaleStart);
    img.setAlpha(1);

    const holdMs = config.durationMs * (1 - config.fadeRatio);
    const fadeMs = config.durationMs * config.fadeRatio;

    // Grow in
    this.scene.tweens.add({
      targets: img,
      scaleX: scaleFull,
      scaleY: scaleFull,
      duration: Math.min(holdMs, 80),
      ease: 'Back.easeOut',
      onComplete: () => {
        // Hold, then fade
        this.scene.tweens.add({
          targets: img,
          alpha: 0,
          duration: fadeMs,
          delay: Math.max(0, holdMs - 80),
          ease: 'Quad.easeIn',
          onComplete: () => this.deactivate(img),
        });
      },
    });
  }

  stop(): void {
    for (const img of this.pool) {
      if (img.active) {
        this.scene.tweens.killTweensOf(img);
        this.scene.tweens.add({
          targets: img,
          alpha: 0,
          duration: 100,
          onComplete: () => this.deactivate(img),
        });
      }
    }
  }

  destroy(): void {
    if (this.textureKey && this.scene.textures.exists(this.textureKey)) {
      this.scene.textures.remove(this.textureKey);
    }
    super.destroy();
  }

  /** Create a canvas texture with a white arc stroke centered at origin. */
  private ensureArcTexture(): string {
    const { arcRadius, arcWidth, sweepAngle } = this.config;
    const r = Math.ceil(arcRadius * CRISP_SCALE);
    const w = Math.ceil(arcWidth * CRISP_SCALE);
    const key = `_slash-arc-r${r}-w${w}-a${Math.round(sweepAngle * 100)}`;

    if (this.scene.textures.exists(key)) return key;

    const size = (r + w) * 2 + 4;
    const cx = size / 2;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    // Draw a white arc stroke, symmetric around angle 0 (pointing right).
    // Tapered ends via round lineCap give a natural trail look.
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = w;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.arc(cx, cx, r, -sweepAngle / 2, sweepAngle / 2);
    ctx.stroke();

    this.scene.textures.addCanvas(key, canvas);
    this.scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
    return key;
  }
}
